import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Lightbulb, Send, TriangleAlert } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useApplicantCoach } from "./ApplicantCoachContext";
import ApplicantCoachAvatar from "./ApplicantCoachAvatar";

const COACH_BRAND = "KI/AI Coach";

const QUICK_ACTIONS = {
  de: [
    { key: "next_step", label: "Was ist jetzt mein nächster Schritt?" },
    { key: "why_not_visible", label: "Warum bin ich noch nicht sichtbar?" },
    { key: "missing_items", label: "Welche Angaben oder Unterlagen fehlen?" },
    { key: "fastest_progress", label: "Was bringt mich jetzt am schnellsten weiter?" },
    { key: "why_this_opportunity", label: "Warum passt diese Chance zu mir?" },
    { key: "why_not_ready_for_opportunity", label: "Warum kann ich diese Chance noch nicht voll nutzen?" },
    { key: "explain_status", label: "Erkläre mir meinen aktuellen Stand" },
    { key: "what_to_add_next", label: "Was sollte ich als Nächstes ergänzen?" },
    { key: "language_status", label: "Erkläre mir meinen Sprachstatus" },
    { key: "visibility_gain", label: "Wodurch werde ich sichtbarer?" },
    { key: "profile_missing_prompt", label: "Welche Angaben fehlen mir hier?" },
    { key: "profile_next_prompt", label: "Was sollte ich als Nächstes ergänzen?" },
    { key: "profile_visibility_prompt", label: "Welche Angaben verbessern meine Sichtbarkeit?" },
    { key: "application_add_next_prompt", label: "Was sollte ich hier als Nächstes ergänzen?" },
    { key: "application_missing_prompt", label: "Welche Angaben fehlen mir in der Bewerbung?" },
    { key: "application_next_prompt", label: "Was ist hier der nächste Schritt?" },
    { key: "documents_missing_prompt", label: "Welche Unterlagen oder Antworten fehlen hier?" },
    { key: "documents_next_prompt", label: "Was ist bei den Unterlagen als Nächstes wichtig?" },
    { key: "documents_status_prompt", label: "Erkläre mir den sichtbaren Unterlagenstand" },
    { key: "b1_status_prompt", label: "Wie ist mein B1-Stand gerade?" },
    { key: "b1_next_prompt", label: "Was ist beim Sprachtest mein nächster Schritt?" },
    { key: "b1_visibility_prompt", label: "Was bringt mir beim Sprachtest mehr Sichtbarkeit?" },
    { key: "account_language", label: "Welche Sprache ist aktuell gesetzt?" },
    { key: "account_setting_meaning", label: "Warum ist diese Einstellung wichtig?" },
    { key: "account_settings_check", label: "Was sollte ich hier prüfen?" },
    { key: "chance_fit_prompt", label: "Warum passt diese Chance zu mir?" },
    { key: "chance_missing_prompt", label: "Was fehlt noch für diese Chance?" },
    { key: "chance_priority", label: "Welche Chance ist am wichtigsten?" },
    { key: "message_priority", label: "Was ist hier am wichtigsten?" },
    { key: "message_open", label: "Welche Nachricht ist offen?" },
    { key: "message_next_prompt", label: "Was sollte ich als Nächstes beachten?" },
    { key: "achievement_progress", label: "Was wurde bereits erreicht?" },
    { key: "achievement_next_milestone", label: "Was ist mein nächster Meilenstein?" },
    { key: "achievement_next_prompt", label: "Was bringt mich hier als Nächstes weiter?" },
  ],
  en: [
    { key: "next_step", label: "What is my next step now?" },
    { key: "why_not_visible", label: "Why am I not visible yet?" },
    { key: "missing_items", label: "What details or documents are missing?" },
    { key: "fastest_progress", label: "What moves me forward fastest now?" },
    { key: "why_this_opportunity", label: "Why does this opportunity fit me?" },
    { key: "why_not_ready_for_opportunity", label: "Why can I not fully use this opportunity yet?" },
    { key: "explain_status", label: "Explain my current status" },
    { key: "what_to_add_next", label: "What should I add next?" },
    { key: "language_status", label: "Explain my language status" },
    { key: "visibility_gain", label: "What makes me more visible?" },
    { key: "profile_missing_prompt", label: "Which profile details are missing here?" },
    { key: "profile_next_prompt", label: "What should I add next here?" },
    { key: "profile_visibility_prompt", label: "Which details improve my visibility?" },
    { key: "application_add_next_prompt", label: "What should I add next in my application?" },
    { key: "application_missing_prompt", label: "Which application details are still missing?" },
    { key: "application_next_prompt", label: "What is the next step here?" },
    { key: "documents_missing_prompt", label: "Which documents or answers are still missing here?" },
    { key: "documents_next_prompt", label: "What matters next in documents?" },
    { key: "documents_status_prompt", label: "Explain my visible document status" },
    { key: "b1_status_prompt", label: "What is my current B1 status?" },
    { key: "b1_next_prompt", label: "What is my next step in the language test?" },
    { key: "b1_visibility_prompt", label: "What makes the language test help my visibility?" },
    { key: "account_language", label: "Which communication language is currently set?" },
    { key: "account_setting_meaning", label: "Why is this setting important?" },
    { key: "account_settings_check", label: "What should I review here?" },
    { key: "chance_fit_prompt", label: "Why does this opportunity fit me?" },
    { key: "chance_missing_prompt", label: "What is still missing for this opportunity?" },
    { key: "chance_priority", label: "Which opportunity matters most right now?" },
    { key: "message_priority", label: "What matters most here?" },
    { key: "message_open", label: "Which message is still open?" },
    { key: "message_next_prompt", label: "What should I pay attention to next?" },
    { key: "achievement_progress", label: "What have I already achieved?" },
    { key: "achievement_next_milestone", label: "What is my next milestone?" },
    { key: "achievement_next_prompt", label: "What helps me move forward from here?" },
  ],
  fr: [
    { key: "next_step", label: "Quelle est ma prochaine étape ?" },
    { key: "why_not_visible", label: "Pourquoi ne suis-je pas encore visible ?" },
    { key: "missing_items", label: "Quelles informations ou pièces manquent ?" },
    { key: "fastest_progress", label: "Qu'est-ce qui me fait avancer le plus vite ?" },
    { key: "why_this_opportunity", label: "Pourquoi cette opportunité me correspond ?" },
    { key: "why_not_ready_for_opportunity", label: "Pourquoi ne puis-je pas encore en profiter pleinement ?" },
    { key: "explain_status", label: "Explique mon statut actuel" },
    { key: "what_to_add_next", label: "Que dois-je compléter ensuite ?" },
    { key: "language_status", label: "Explique mon statut linguistique" },
    { key: "visibility_gain", label: "Comment devenir plus visible ?" },
  ],
  tr: [
    { key: "next_step", label: "Şimdi bir sonraki adımım ne?" },
    { key: "why_not_visible", label: "Neden henüz görünür değilim?" },
    { key: "missing_items", label: "Hangi bilgi veya belgeler eksik?" },
    { key: "fastest_progress", label: "Şimdi beni en hızlı ne ilerletir?" },
    { key: "why_this_opportunity", label: "Bu fırsat neden bana uygun?" },
    { key: "why_not_ready_for_opportunity", label: "Neden bu fırsatı henüz tam kullanamıyorum?" },
    { key: "explain_status", label: "Mevcut durumumu açıkla" },
    { key: "what_to_add_next", label: "Sırada ne eklemeliyim?" },
    { key: "language_status", label: "Dil durumumu açıkla" },
    { key: "visibility_gain", label: "Beni daha görünür yapan nedir?" },
  ],
  vi: [
    { key: "next_step", label: "Bước tiếp theo của tôi là gì?" },
    { key: "why_not_visible", label: "Tại sao tôi chưa hiển thị?" },
    { key: "missing_items", label: "Còn thiếu thông tin hoặc giấy tờ nào?" },
    { key: "fastest_progress", label: "Điều gì giúp tôi tiến nhanh nhất bây giờ?" },
    { key: "why_this_opportunity", label: "Vì sao cơ hội này phù hợp với tôi?" },
    { key: "why_not_ready_for_opportunity", label: "Vì sao tôi chưa thể tận dụng cơ hội này hoàn toàn?" },
    { key: "explain_status", label: "Giải thích trạng thái hiện tại của tôi" },
    { key: "what_to_add_next", label: "Tiếp theo tôi nên bổ sung gì?" },
    { key: "language_status", label: "Giải thích trạng thái ngôn ngữ của tôi" },
    { key: "visibility_gain", label: "Điều gì giúp tôi hiển thị hơn?" },
  ],
  uk: [
    { key: "next_step", label: "Який мій наступний крок?" },
    { key: "why_not_visible", label: "Чому мене ще не видно?" },
    { key: "missing_items", label: "Яких даних або документів не вистачає?" },
    { key: "fastest_progress", label: "Що зараз рухає мене найшвидше?" },
    { key: "why_this_opportunity", label: "Чому ця можливість мені підходить?" },
    { key: "why_not_ready_for_opportunity", label: "Чому я ще не можу повністю використати цю можливість?" },
    { key: "explain_status", label: "Поясни мій поточний статус" },
    { key: "what_to_add_next", label: "Що мені додати далі?" },
    { key: "language_status", label: "Поясни мій мовний статус" },
    { key: "visibility_gain", label: "Що робить мене більш видимим?" },
  ],
  bks: [
    { key: "next_step", label: "Koji je moj sljedeci korak?" },
    { key: "why_not_visible", label: "Zasto jos nisam vidljiv?" },
    { key: "missing_items", label: "Koji podaci ili dokumenti nedostaju?" },
    { key: "fastest_progress", label: "Sta me sada najbrze pomjera naprijed?" },
    { key: "why_this_opportunity", label: "Zasto mi ova prilika odgovara?" },
    { key: "why_not_ready_for_opportunity", label: "Zasto jos ne mogu potpuno iskoristiti ovu priliku?" },
    { key: "explain_status", label: "Objasni mi moj trenutni status" },
    { key: "what_to_add_next", label: "Sta trebam sljedece dopuniti?" },
    { key: "language_status", label: "Objasni mi moj jezicki status" },
    { key: "visibility_gain", label: "Sta me cini vidljivijim?" },
  ],
  ar: [
    { key: "next_step", label: "ما هي خطوتي التالية الآن؟" },
    { key: "why_not_visible", label: "لماذا لست مرئياً بعد؟" },
    { key: "missing_items", label: "ما المعلومات أو المستندات الناقصة؟" },
    { key: "fastest_progress", label: "ما الذي يدفعني للأمام بأسرع شكل الآن؟" },
    { key: "why_this_opportunity", label: "لماذا تناسبني هذه الفرصة؟" },
    { key: "why_not_ready_for_opportunity", label: "لماذا لا أستطيع الاستفادة الكاملة من هذه الفرصة بعد؟" },
    { key: "explain_status", label: "اشرح لي وضعي الحالي" },
    { key: "what_to_add_next", label: "ماذا يجب أن أضيف بعد ذلك؟" },
    { key: "language_status", label: "اشرح لي حالة اللغة" },
    { key: "visibility_gain", label: "ما الذي يجعلني أكثر ظهوراً؟" },
  ],
};

const SURFACE_ACTIONS = {
  applicant_dashboard: [
    { key: "next_step" },
    { key: "why_not_visible" },
    { key: "missing_items" },
    { key: "fastest_progress" },
    { key: "visibility_gain" },
  ],
  application: [
    { key: "what_to_add_next", labelKey: "application_add_next_prompt" },
    { key: "missing_items", labelKey: "application_missing_prompt" },
    { key: "next_step", labelKey: "application_next_prompt" },
    { key: "visibility_gain" },
  ],
  documents: [
    { key: "missing_items", labelKey: "documents_missing_prompt" },
    { key: "next_step", labelKey: "documents_next_prompt" },
    { key: "explain_status", labelKey: "documents_status_prompt" },
  ],
  language_test_b1: [
    { key: "language_status", labelKey: "b1_status_prompt" },
    { key: "next_step", labelKey: "b1_next_prompt" },
    { key: "visibility_gain", labelKey: "b1_visibility_prompt" },
    { key: "explain_status" },
  ],
  profile: [
    { key: "missing_items", labelKey: "profile_missing_prompt" },
    { key: "what_to_add_next", labelKey: "profile_next_prompt" },
    { key: "visibility_gain", labelKey: "profile_visibility_prompt" },
  ],
  account: [
    { key: "account_language" },
    { key: "account_setting_meaning" },
    { key: "account_settings_check" },
  ],
  matches: [
    { key: "why_this_opportunity", labelKey: "chance_fit_prompt" },
    { key: "why_not_ready_for_opportunity", labelKey: "chance_missing_prompt" },
    { key: "chance_priority" },
  ],
  messages: [
    { key: "message_priority" },
    { key: "message_open" },
    { key: "next_step", labelKey: "message_next_prompt" },
  ],
  achievements: [
    { key: "achievement_progress" },
    { key: "achievement_next_milestone" },
    { key: "next_step", labelKey: "achievement_next_prompt" },
  ],
  default: [
    { key: "next_step" },
    { key: "missing_items" },
    { key: "explain_status" },
  ],
};

const PANEL_TEXT = {
  de: {
    toggle: COACH_BRAND,
    kicker: COACH_BRAND,
    title: "Hilfe zu deinem sichtbaren Stand",
    notice: "Der Coach erklärt nur deinen sichtbaren Stand, führt keine Aktionen aus und ersetzt keine Systemlogik.",
    context: "Kontext",
    waiting: "Der Coach wartet auf einen stabilen sichtbaren Applicant-Kontext.",
    intro: "Stelle eine Frage oder nutze eine Schnellhilfe. Antworten bleiben strikt im freigegebenen Applicant-Kontext.",
    loading: "Coach analysiert deinen sichtbaren Stand ...",
    ask: "Fragen",
    placeholder: "Frage zu deinem Stand, zu Blockern oder zu Chancen stellen",
    nextStep: "Nächster Schritt",
  },
  en: {
    toggle: COACH_BRAND,
    kicker: COACH_BRAND,
    title: "Help with your visible status",
    notice: "The coach explains your visible status only, does not execute actions, and does not replace system logic.",
    context: "Context",
    waiting: "The coach is waiting for a stable visible applicant context.",
    intro: "Ask a question or use a quick action. Replies stay strictly inside the released applicant context.",
    loading: "Coach is analysing your visible status ...",
    ask: "Ask",
    placeholder: "Ask about your status, blockers, or opportunities",
    nextStep: "Next step",
  },
  fr: {
    toggle: COACH_BRAND,
    kicker: COACH_BRAND,
    title: "Aide sur ton statut visible",
    notice: "Le coach explique seulement ton statut visible, n'exécute aucune action et ne remplace pas la logique du système.",
    context: "Contexte",
    waiting: "Le coach attend un contexte applicant visible et stable.",
    intro: "Pose une question ou utilise une aide rapide. Les réponses restent strictement dans le contexte applicant autorisé.",
    loading: "Le coach analyse ton statut visible ...",
    ask: "Demander",
    placeholder: "Pose une question sur ton statut, tes blocages ou tes opportunités",
    nextStep: "Prochaine étape",
  },
  tr: {
    toggle: COACH_BRAND,
    kicker: COACH_BRAND,
    title: "Gorunur durumun icin yardim",
    notice: "Koc sadece gorunur durumunu aciklar, islem yapmaz ve sistem mantiginin yerine gecmez.",
    context: "Baglam",
    waiting: "Koc sabit ve gorunur bir applicant baglami bekliyor.",
    intro: "Bir soru sor veya hizli yardimlardan birini kullan. Yanıtlar sadece izin verilen applicant baglaminda kalir.",
    loading: "Koc gorunur durumunu analiz ediyor ...",
    ask: "Sor",
    placeholder: "Durumun, engellerin veya firsatlarin hakkinda soru sor",
    nextStep: "Sonraki adim",
  },
  vi: {
    toggle: COACH_BRAND,
    kicker: COACH_BRAND,
    title: "Ho tro ve trang thai dang hien thi cua ban",
    notice: "Coach chi giai thich trang thai dang hien thi, khong thuc hien hanh dong va khong thay the logic he thong.",
    context: "Ngu canh",
    waiting: "Coach dang cho mot ngu canh applicant on dinh va dang hien thi.",
    intro: "Hay dat cau hoi hoac dung mot thao tac nhanh. Cau tra loi se chi nam trong ngu canh applicant duoc phep.",
    loading: "Coach dang phan tich trang thai dang hien thi cua ban ...",
    ask: "Hoi",
    placeholder: "Hoi ve trang thai, diem chan hoac co hoi cua ban",
    nextStep: "Buoc tiep theo",
  },
  uk: {
    toggle: COACH_BRAND,
    kicker: COACH_BRAND,
    title: "Dopomoha z tvoyim vydymym statusom",
    notice: "Coach poyasnyuye lyshe vydymyy status, ne vykonuye diy i ne zaminyuye logiku systemy.",
    context: "Kontekst",
    waiting: "Coach ochikuye na stabilnyy vydymyy applicant-kontekst.",
    intro: "Postav pytannya abo vykorystay shvydku diyu. Vidpovidi zalyshayutsya lyshe v dozvolenomu applicant-konteksti.",
    loading: "Coach analizuye tviy vydymyy status ...",
    ask: "Zapytaty",
    placeholder: "Zapytay pro sviy status, blokery abo mozhlyvosti",
    nextStep: "Nastupnyy krok",
  },
  bks: {
    toggle: COACH_BRAND,
    kicker: COACH_BRAND,
    title: "Pomoc za tvoj vidljivi status",
    notice: "Coach objasnjava samo tvoj vidljivi status, ne izvrsava akcije i ne mijenja logiku sistema.",
    context: "Kontekst",
    waiting: "Coach ceka stabilan vidljivi applicant kontekst.",
    intro: "Postavi pitanje ili koristi brzu pomoc. Odgovori ostaju strogo u dozvoljenom applicant kontekstu.",
    loading: "Coach analizira tvoj vidljivi status ...",
    ask: "Pitaj",
    placeholder: "Pitaj o svom statusu, blockerima ili prilikama",
    nextStep: "Sljedeci korak",
  },
  ar: {
    toggle: COACH_BRAND,
    kicker: COACH_BRAND,
    title: "مساعدة بخصوص وضعك الظاهر",
    notice: "المدرب يشرح فقط وضعك الظاهر، ولا ينفذ إجراءات، ولا يستبدل منطق النظام.",
    context: "السياق",
    waiting: "المدرب ينتظر سياق Applicant ثابتاً وواضحاً.",
    intro: "اطرح سؤالاً أو استخدم إحدى المساعدات السريعة. تبقى الإجابات داخل سياق Applicant المسموح فقط.",
    loading: "المدرب يحلل وضعك الظاهر الآن ...",
    ask: "اسأل",
    placeholder: "اسأل عن وضعك أو العوائق أو الفرص",
    nextStep: "الخطوة التالية",
  },
};

function getLanguageCode(replyLanguage) {
  return replyLanguage?.code || "en";
}

function getPanelText(languageCode) {
  return PANEL_TEXT[languageCode] || PANEL_TEXT.en;
}

function getQuickActionLabel(languageCode, key) {
  const localized = QUICK_ACTIONS[languageCode] || QUICK_ACTIONS.en;
  return localized.find((item) => item.key === key)?.label
    || QUICK_ACTIONS.en.find((item) => item.key === key)?.label
    || key;
}

function getSurfaceActionKey(coachSurfaceContext = {}) {
  return coachSurfaceContext?.surface_summary?.surface_type
    || coachSurfaceContext?.screen_key
    || "default";
}

function getQuickActions(languageCode, coachSurfaceContext = {}) {
  const surfaceActionKey = getSurfaceActionKey(coachSurfaceContext);
  const configs = SURFACE_ACTIONS[surfaceActionKey] || SURFACE_ACTIONS.default;
  return configs.map((config) => ({
    key: config.key,
    label: getQuickActionLabel(languageCode, config.labelKey || config.key),
  }));
}

export default function ApplicantCoachPanel() {
  const {
    coachOpen,
    setCoachOpen,
    coachLoading,
    coachError,
    coachMessages,
    coachSurfaceContext,
    askApplicantCoach,
    replyLanguage,
  } = useApplicantCoach();
  const [question, setQuestion] = useState("");
  const languageCode = getLanguageCode(replyLanguage);
  const ui = getPanelText(languageCode);
  const quickActions = useMemo(
    () => getQuickActions(languageCode, coachSurfaceContext),
    [coachSurfaceContext, languageCode]
  );
  const hasStableContext = Boolean(coachSurfaceContext?.screen_key);

  const handleQuickAction = async (action) => {
    if (!hasStableContext) return;
    await askApplicantCoach({
      question: action.label,
      quickAction: action.key,
    });
    setCoachOpen(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextQuestion = question.trim();
    if (!nextQuestion || !hasStableContext) return;
    await askApplicantCoach({ question: nextQuestion, quickAction: "" });
    setQuestion("");
    setCoachOpen(true);
  };

  return (
    <aside
      className={`applicant-coach${coachOpen ? " is-open" : ""}`}
      aria-hidden={!coachOpen}
      dir={replyLanguage?.direction || "ltr"}
    >
      <button
        type="button"
        className="applicant-coach__toggle"
        onClick={() => setCoachOpen((current) => !current)}
        aria-expanded={coachOpen}
        aria-controls="applicant-coach-panel"
      >
        <ApplicantCoachAvatar size={34} className="applicant-coach__toggle-avatar" />
        <span>{ui.toggle}</span>
        {coachOpen ? <ChevronRight size={16} strokeWidth={2} /> : <ChevronLeft size={16} strokeWidth={2} />}
      </button>

      <div className="applicant-coach__panel" id="applicant-coach-panel">
        <div className="applicant-coach__head">
          <ApplicantCoachAvatar size={56} className="applicant-coach__head-avatar" />
          <div>
            <p className="applicant-coach__kicker">{ui.kicker}</p>
            <h3>{ui.title}</h3>
            <p>{coachSurfaceContext?.screen_label || "Applicant Dashboard"} · {replyLanguage?.label || "English"}</p>
          </div>
        </div>

        <div className="applicant-coach__notice">
          <TriangleAlert size={15} strokeWidth={2} />
          <span>{ui.notice}</span>
        </div>

        <div className="applicant-coach__quick-actions">
          {quickActions.map((action) => (
            <button
              key={action.key}
              type="button"
              className="applicant-coach__quick-action"
              disabled={coachLoading || !hasStableContext}
              onClick={() => handleQuickAction(action)}
            >
              <Lightbulb size={14} strokeWidth={2} />
              <span>{action.label}</span>
            </button>
          ))}
        </div>

        <div className="applicant-coach__context">
          <span>{ui.context}</span>
          <strong>{coachSurfaceContext?.screen_label || "Applicant Dashboard"}</strong>
          <p>{coachSurfaceContext?.highlighted_step || ui.waiting}</p>
        </div>

        <div className="applicant-coach__feed">
          {!hasStableContext ? (
            <p className="applicant-coach__empty">{ui.waiting}</p>
          ) : null}
          {hasStableContext && !coachMessages.length ? (
            <p className="applicant-coach__empty">{ui.intro}</p>
          ) : null}
          {coachMessages.map((message) => (
            <article key={message.id} className="applicant-coach__message" dir={message.reply_direction || replyLanguage?.direction || "ltr"}>
              {message.question ? <p className="applicant-coach__question">{message.question}</p> : null}
              <p className="applicant-coach__answer">{message.answer}</p>
              {message.recommended_next_step ? (
                <p className="applicant-coach__next-step">
                  {ui.nextStep}: <strong>{message.recommended_next_step}</strong>
                </p>
              ) : null}
              {message.caution ? (
                <p className="applicant-coach__caution">{message.caution}</p>
              ) : null}
            </article>
          ))}
          {coachLoading ? <p className="applicant-coach__empty">{ui.loading}</p> : null}
          {coachError ? <p className="applicant-coach__error">{coachError}</p> : null}
        </div>

        <form className="applicant-coach__form" onSubmit={handleSubmit}>
          <Input
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            className="dashboard-field"
            placeholder={ui.placeholder}
            disabled={coachLoading || !hasStableContext}
          />
          <Button type="submit" className="dashboard-inline-button" disabled={coachLoading || !hasStableContext || !question.trim()}>
            <Send className="mr-2 h-4 w-4" />
            {ui.ask}
          </Button>
        </form>
      </div>
    </aside>
  );
}
